import { Alert, Form } from 'antd';
import { EmptyState } from '@/components/EmptyState';
import { ReorderableList } from '@/components/ReorderableList';
import { DutyPointCard } from '@/components/intake/DutyPointCard';
import { sectorOptionsFor, zoneOptionsFor } from '@/components/intake/draft';
import { lineKeyOf } from '@pams/shared';
import { DUTY_COPY, SHARE_COPY } from '../copy';
import type { ArrangementSectionActions, ArrangementSectionState } from '../sections';

/**
 * ─── THE DUTY POINT STACK ────────────────────────────────────────────────────────
 *
 * ⚠️ IT LIVES IN A COMPONENT FILE BECAUSE `sections.tsx` MAY NOT DECLARE ONE.
 * `react-refresh/only-export-components` is a warning and `pnpm lint` runs
 * `--max-warnings 0`, so a file that exports data and also declares a PascalCase component
 * fails the build. The registry stays data; this renders it.
 *
 * ⚠️ THERE IS NO OUTER `Form.List`, AND REMOVING IT FIXED A REAL BUG.
 *
 * One wrapped this stack at first, on the assumption that it was what registered each
 * card's fields. It is not — every control inside a card addresses an ABSOLUTE path
 * (`['dutyPoints', index, …]`) and registers itself. What the wrapper actually did was nest
 * `PlacesSection`'s own `Form.List` inside another one bound to an overlapping path, and
 * the inner list then saw ZERO rows: every card rendered its "add the place" empty state
 * with a place already in the store behind it. Plain `Form.Item`s were unaffected, which is
 * what made it look like a `PlacesSection` bug rather than a nesting one.
 *
 * ⚠️ REORDERING WRITES THE WHOLE ARRAY, which is exactly why each card carries a MINTED
 * `id`: `Form.Item` names ARE indices, so moving card 3 to position 1 by index alone would
 * land card 3's grid on card 1. The id is what the deployment keys on, and it travels.
 */
export const DutyPointStack = ({
  state,
  actions,
}: {
  state: ArrangementSectionState;
  actions: ArrangementSectionActions;
}) => (
  <div className="flex flex-col gap-3" data-testid="duty-point-stack">
    {/*
      ⚠️ REGISTERED AS A HIDDEN FIELD, AND THIS IS NOT OPTIONAL.
      `Form.useWatch`'s selector is called with `getFieldsValue()`, which answers REGISTERED
      FIELDS ONLY — so with nothing registering `dutyPoints` itself, the page's whole card
      derivation silently read an empty array and NO card ever rendered. It is a SIBLING of
      the cards rather than a wrapper, which is what keeps `PlacesSection`'s own `Form.List`
      out of a nested path (see this file's header).
    */}
    <Form.Item name="dutyPoints" hidden>
      <input type="hidden" />
    </Form.Item>
    {/*
      ⚠️ [C5c §2] AND `sharedColumns` BESIDE IT, FOR THE SAME REASON. Unregistered, the path is
      absent from the watched draft (so no card shows a dash) and from `onFinish` — and the page's
      `body()` reads `getFieldsValue(true)`, which would still send it, so the two would disagree
      about whether anything is shared. [P3a.1]'s trap, one field over.
    */}
    <Form.Item name="sharedColumns" hidden>
      <input type="hidden" />
    </Form.Item>

    {/*
      ⚠️ [C3c §9b] THE REASON IS ON SCREEN, not only in a disabled control. A control that is
      unavailable without saying why reads as broken; and the server has refused these edits with
      a 409 since [P5a], so an operator could previously only discover the rule by losing work to
      it.
    */}
    {state.dutyPointsLocked && (
      <Alert type="info" showIcon data-testid="duty-points-locked" message={DUTY_COPY.structureLocked} />
    )}
    {state.dutyPoints.length === 0 && <EmptyState size="sm" title={DUTY_COPY.empty} />}
    <ReorderableList
      ariaLabel="Duty points"
      items={state.dutyPoints.map((point) => ({
        id: point.id,
        label: point.draft.locationNames[0] ?? `Duty point ${String(point.index + 1)}`,
      }))}
      /* ⚠️ [C3c §9b] Reordering changes the duty-point SET, which the server freezes. */
      disabled={state.readOnly || state.dutyPointsLocked}
      /*
       * ⚠️ THE WHOLE ARRAY, KEYED BY MINTED ID. `Form.Item` names ARE indices, so moving
       * card 3 to position 1 by index alone would land card 3's grid on card 1. The
       * page re-writes `dutyPoints` in the new order and the ids travel with them.
       */
      onReorder={(next) => {
        actions.reorderDutyPoints(next.map((row) => row.id));
      }}
      /*
       * ⚠️ [D4.1] `'block'`, SO THE CARD GETS THE MEASURE. In the default row layout the
       * controls sit in a flex row beside the content and a whole duty point card was
       * squeezed into 420 of 604px at the 1366 floor — with `truncate` on it. Here the
       * `<li>` is bare and `controls` goes into the card's OWN header, which is also where
       * an operator expects to find reordering on an accordion.
       */
      layout="block"
      renderItem={(item, _index, controls) => {
        const point = state.dutyPoints.find((row) => row.id === item.id);
        if (!point) return null;
        return (
          <DutyPointCard
            controls={controls}
            index={point.index}
            id={point.id}
            draft={point.draft}
            shape={point.shape}
            slots={point.slots}
            grid={point.grid}
            roles={point.roles}
            policies={state.policies}
            resolvePolicy={state.resolvePolicy}
            policyName={point.policyName}
            policyVersionLabel={point.policyVersionLabel}
            officers={state.officers}
            orgUnits={state.orgUnits}
            /* ⚠️ [C6a §2] THIS card's asks only — an ask id is unique on its duty point, not the order. */
            commandAsks={state.commandAsks.get(point.id)}
            awaitedLive={state.awaitedLive}
            forks={point.forks}
            forkColumns={point.forkColumns}
            versionMoved={point.versionMoved}
            open={point.open}
            readOnly={state.readOnly}
            structureLocked={state.dutyPointsLocked}
            /* ⚠️ [C4a §4b] The OTHER cards' zones on this order — never a global list, never its own. */
            zoneOptions={zoneOptionsFor(state.dutyPoints, point.id)}
            /* [C5c §4] And its sectors, by the same rule. */
            sectorOptions={sectorOptionsFor(state.dutyPoints, point.id)}
            onToggle={() => {
              actions.toggleCard(point.id);
            }}
            onDuplicate={() => {
              actions.duplicateDutyPoint(point.index);
            }}
            onRemove={() => {
              actions.removeDutyPoint(point.index);
            }}
            onAddShift={() => {
              actions.appendShift(point.index);
            }}
            onAddPlace={() => {
              actions.appendPlace(point.index);
            }}
            onCommandEntriesChange={actions.commandEntriesChanged}
            onPolicyChange={(policyId) => {
              // ⚠️ [amended C1a] TWO ARGUMENTS. The third was a `policyVersionId` the
              // implementation never took and this call site passed `''` into — which read
              // as a deliberate version pin in the one screen where that is the whole
              // question. Choosing a policy clears the pin; the server resolves.
              actions.setPolicy(point.index, policyId);
            }}
            onRequirementLines={(lines) => {
              actions.setRequirementLines(point.index, lines);
            }}
            onUseCurrentColumns={() => {
              actions.useCurrentColumns(point.index);
            }}
            onAddAdHoc={(label) => {
              actions.addAdHocColumn(point.index, label);
            }}
            onRestrictAdHoc={(lineIndex, postTypeIds) => {
              actions.restrictAdHocColumn(point.index, lineIndex, postTypeIds);
            }}
            postTypeOptions={point.postTypeOptions}
            onRemoveAdHoc={(lineIndex) => {
              actions.removeAdHocColumn(point.index, lineIndex);
            }}
            coveredColumns={point.coveredColumns}
            sharedLineIndexes={point.sharedLineIndexes}
            /* ⚠️ [C5c §2a] ABSENT, not disabled, where sharing is not offered — see `ForceGrid`. */
            onToggleShare={
              point.shareOffered
                ? (lineIndex) => {
                    actions.toggleShare(point.index, lineIndex);
                  }
                : undefined
            }
            refuseAdHoc={(label) => {
              const hit = point.coveredColumns.find(
                (column) => column.lineKey === lineKeyOf({ postTypeId: null, vehicleTypeId: null, adHocLabel: label }),
              );
              return hit ? SHARE_COPY.adHocCovered(hit.label, hit.anchorLabel) : undefined;
            }}
            policyLockedBySharing={point.policyLockedBySharing}
          />
        );
      }}
    />
  </div>
);
